import { IconSet } from '@postscript/components';
import { GlobalBanner } from 'components/GlobalBanners/globalBanners';
import { Notification as LegacyNotification } from 'controllers/contexts/legacyGlobalNotifications';
import { NotificationComponentProps } from './NotificationComponent';

/**
 * Maps notification variants to their corresponding icons based on Figma design
 */
const getVariantIcon = (variant?: string) => {
  switch (variant) {
    case 'default':
      return IconSet.Information; // Blue circle with 'i'
    case 'success':
      return IconSet.CheckCircle; // Green circle with check
    case 'warning':
      return IconSet.WarningTriangle; // Yellow triangle with !
    case 'critical':
      return IconSet.Forbid; // Red circle with slash
    case 'guidance':
      return IconSet.Bulb; // Purple light bulb
    default:
      // Fallback to warning triangle for unknown variants
      return IconSet.WarningTriangle;
  }
};

/**
 * Converts a GlobalBanner to NotificationComponent props
 */
export const convertGlobalBannerToNotification = (
  notification: GlobalBanner,
): NotificationComponentProps => {
  const { id, isDismissable, customIcon, data } = notification;

  return {
    id,
    heading: data.heading || 'Notification',
    description: data.bodyText || undefined,
    icon: customIcon || getVariantIcon(data.variant),
    primaryAction: data.primaryAction
      ? {
          text: data.primaryAction.text || 'Action',
          onClick: data.primaryAction.onClick,
        }
      : undefined,
    secondaryAction: data.secondaryAction
      ? {
          text: data.secondaryAction.text || 'Cancel',
          onClick: data.secondaryAction.onClick,
        }
      : undefined,
    showButtons: !!(data.primaryAction || data.secondaryAction),
    dismissible: isDismissable || !!data.onDismiss,
    'data-cy': `notification-${id}`,
  };
};

/**
 * Converts a LegacyNotification to NotificationComponent props
 */
export const convertLegacyBannerToNotification = (
  notification: LegacyNotification,
): NotificationComponentProps => {
  const { id, data, message } = notification;

  // Use message from data.message or notification.message, fallback to content if it's a string
  const notificationMessage =
    data.message ||
    message ||
    (typeof data.content === 'string' ? data.content : 'Notification');

  return {
    id,
    heading: undefined,
    description: notificationMessage || undefined,
    icon: IconSet.WarningTriangle,
    primaryAction: data.primaryAction
      ? {
          text: data.primaryAction.text || 'Action',
          onClick: data.primaryAction.onClick,
        }
      : undefined,
    showButtons: !!data.primaryAction,
    dismissible: data.closeable,
    'data-cy': `legacy-notification-${id}`,
  };
};
